import React, { useCallback, useContext, useMemo, useRef } from "react"
import classNames from "classnames"
import dayjs from "dayjs"
import { SafeAreaView } from "@hg/layouts"

import { useETL, useTranslation } from "@/locales"
import { GlobalContext } from "@/topic/rogue_5/context"
import { SFX } from "@/topic/rogue_5/utils/sfx"
import { useGameStore } from "../../store"
import { useCopperModal } from "../Modal"
import { useLotteryModal } from "../Modal/LotteryModal"
import { useRecordModal } from "../Modal/RecordModal"

import styles from "./index.module.less"

const Menu: React.FC<{
    back?: () => void
    className?: string
    giftEntryActive?: boolean
}> = (props) => {
    const { className, back, giftEntryActive } = props
    const callLotteryModal = useLotteryModal()

    const {
        state: { bgm },
        dispatch,
    } = useContext(GlobalContext)

    const handleClickHome = useCallback(() => {
        back?.()
    }, [back])

    const handleClickMusic = useCallback(() => {
        dispatch({
            type: "updateContext",
            payload: { bgm: !bgm },
        })
    }, [dispatch, bgm])

    const containerRef = useRef<HTMLDivElement>(null)

    const giftActive = useMemo(
        () => giftEntryActive && dayjs().unix() < __ROGUE_5_LOTTERY_FILL_END_AT,
        [giftEntryActive]
    )

    return (
        <SafeAreaView
            ref={containerRef}
            mode="margin"
            edges={["right"]}
            className={classNames(styles.menu, className)}
        >
            <div
                className={classNames(
                    styles.menuItem,
                    styles.gift,
                    !giftActive && styles.hidden
                )}
                onClick={(e) => {
                    e.stopPropagation()
                    SFX.play("click")
                    callLotteryModal()
                }}
            />
            <div
                className={classNames(styles.menuItem, styles.home)}
                onClick={(e) => {
                    e.stopPropagation()
                    SFX.play("click")
                    handleClickHome()
                }}
            />
            <div
                className={classNames(
                    styles.menuItem,
                    styles.bgm,
                    !bgm && styles.off
                )}
                onClick={(e) => {
                    e.stopPropagation()
                    SFX.play("click")
                    handleClickMusic()
                }}
            />
        </SafeAreaView>
    )
}

export const BackButton = ({
    onClick,
    className,
}: {
    onClick: () => void
    className?: string
}) => {
    const backCallback = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation()
            SFX.play("click")
            onClick()
        },
        [onClick]
    )
    return (
        <SafeAreaView
            mode="margin"
            edges={["left"]}
            className={classNames(styles.backButton, className)}
        >
            <div
                className={classNames(styles.menuItem, styles.back)}
                onClick={backCallback}
            />
        </SafeAreaView>
    )
}

const RightButton: React.FC<{
    className?: string
    style?: React.CSSProperties
    text: string
    type: "box" | "coins"
    onClick?: () => void
}> = ({ className, style, text, type, onClick }) => {
    return (
        <div
            className={classNames(styles.rightButton, className)}
            style={style}
            onClick={onClick}
        >
            <div className={styles.deco}>
                <div className={styles.light}></div>
                <div className={classNames(styles.icon, styles[type])}></div>
            </div>
            <div className={styles.text}>
                <span>{text}</span>
            </div>
        </div>
    )
}

export const UIButtonLayer: React.FC<{
    className?: string
    style?: React.CSSProperties
    back?: () => void
}> = ({ className, style, back }) => {
    const { t } = useTranslation()
    const callRecordModal = useRecordModal()
    const callCopperModal = useCopperModal()
    const { uiActive, boxButtonActive, giftEntryActive } = useGameStore()
    const boxActive = useMemo(
        () => boxButtonActive && dayjs().unix() < __ROGUE_5_SIGN_IN_END_AT,
        [boxButtonActive]
    )
    const collect = useETL()
    return (
        <div
            className={classNames(styles.uiLayer, uiActive && styles.uiActive)}
        >
            <div
                className={classNames(
                    styles.rightButtonGroup,
                    boxActive && styles.boxButtonActive,
                    className
                )}
                style={style}
            >
                <RightButton
                    text={t("game.rightButton.box")}
                    type="box"
                    onClick={() => {
                        // SFX.play("click")
                        collect("button_click", { button_name: "game_record" })
                        callRecordModal()
                    }}
                />
                <RightButton
                    text={t("game.rightButton.coins")}
                    type="coins"
                    onClick={() => {
                        // SFX.play("click")
                        collect("button_click", { button_name: "game_coins" })
                        callCopperModal()
                    }}
                />
            </div>
            <Menu back={back} giftEntryActive={giftEntryActive} />
        </div>
    )
}
